import { useId, useMemo, useRef } from 'react';

import cs from 'classnames';

// import { formatFileSize } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import { showAlert } from 'components/shared/Alert/Alert';
import ProgressBar from 'components/shared/ProgressBar/ProgressBar';

import { iconForFile } from './lib/constants';
import { toRelativePath } from './lib/useFileUploader';
import { matchesFormats, unsupportedFilesAlert } from './lib/acceptedFormats';

export default function S3Uploader({
  maxFiles = 1,
  formats = '.zip',
  enforceFormats = false,
  title = 'Upload file',
  showButtons = false,
  isUploading = false,
  onUpload,
  onCancel = () => {},
  files = [],
  setFiles = () => {},
  uploadProgressList = []
}) {
  const fileInputRef = useRef(null);
  const fileInputId = useId();
  const allowedFormats = useMemo(() => {
    return formats;
  }, [formats]);
  const addFiles = (incoming) => {
    const accepted = enforceFormats
      ? incoming.filter((file) => matchesFormats(file, formats))
      : incoming;
    const rejected = incoming.filter((file) => !accepted.includes(file));
    if (rejected.length > 0) showAlert(unsupportedFilesAlert(rejected, formats));
    if (accepted.length === 0) return;
    if (files.length + accepted.length > maxFiles) {
      showAlert({
        color: 'danger',
        message: `Maximum ${maxFiles} files allowed`
      });
      return;
    }
    setFiles((prevFiles) => [...prevFiles, ...accepted]);
  };
  const handleFileChange = (e) => {
    addFiles(Array.from(e.target.files));
    e.target.value = '';
  };
  const handleOpenPicker = () => {
    fileInputRef.current.click();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addFiles(Array.from(e.dataTransfer.files));
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const description = `Upload files in the following formats: ${formats}`;
  return (
    <>
      {showButtons && (
        <div className="flex justify-center gap-4">
          <Button
            onClick={onCancel}
            outlined
            text="Cancel"
            className="mt-4"
            disabled={isUploading}
          />
          <Button
            disabled={files.length === 0 || isUploading}
            onClick={() => onUpload(files)}
            text={isUploading ? 'Uploading…' : 'Start uploading'}
            className="mt-4"
          />
        </div>
      )}
      <div>
        <div
          className={cs(
            'border-gray-400 flex h-[200px] w-full cursor-pointer flex-col items-center gap-5 rounded-lg border border-dashed p-8',
            'hover:border-primary-500 hover:bg-primary-25'
          )}
          onClick={handleOpenPicker}
          onDrop={handleDrop}
          onDragOver={handleDragOver}>
          <Icon icon="cloud-upload" />
          <div className="flex flex-col items-center justify-center gap-1.5 p-0">
            <span className="text-gray-900 text-sm font-medium">{title}</span>
            <span className="text-gray-600 text-xs font-normal">{description}</span>
            {maxFiles && (
              <span className="text-gray-600 text-xs font-normal">{`Maximum ${maxFiles} files allowed`}</span>
            )}
          </div>
          <Button variant="tertiary" icon="export-mono" text="Upload" color="neutral" />
        </div>
        <label htmlFor={fileInputId} className="sr-only">
          {title}
        </label>
        <input
          id={fileInputId}
          ref={fileInputRef}
          type="file"
          className="sr-only"
          multiple={maxFiles > 1}
          onChange={handleFileChange}
          accept={allowedFormats}
        />
        {files.length > 0 && (
          <ul className="mt-4 flex flex-col gap-4">
            {files.map((f, index) => {
              // fileKey (folder-relative path) disambiguates same-named files in
              // different sub-folders; producers that only set filename still match.
              const fileProgress = uploadProgressList.find((fp) =>
                fp.fileKey ? fp.fileKey === toRelativePath(f) : fp.filename === f.name
              );
              return (
                <li
                  key={index}
                  className="flex flex-col gap-2 rounded-xl border border-solid border-neutral-200 p-4">
                  <div className="items-top flex justify-between">
                    <div className="flex items-center gap-2">
                      <Icon icon={iconForFile(f.name)} />
                      <div className="flex flex-col gap-2">
                        <span className="font-semibold">{f.name}</span>
                        {/* TODO: format file size to be human readable */}
                        <span className="text-xs">{f.size}</span>
                      </div>
                    </div>
                    <div className="h-fit">
                      <Icon
                        onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                        icon="close-circle-filled"
                        color="danger"
                      />
                    </div>
                  </div>
                  {fileProgress && <ProgressBar progress={fileProgress.progress} />}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}
